/**
 * EventEmptyState.tsx
 *
 * Empty state for "Your Events" tabs.
 */

import React from 'react';
import { FiCalendar } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';

interface EventEmptyStateProps {
  tab: 'all' | 'drafts' | 'published';
}

export const EventEmptyState: React.FC<EventEmptyStateProps> = ({ tab }) => {
  const navigate = useNavigate();

  const config = {
    all: {
      title: 'Bring your next event to life',
      subtitle:
        'Publish concerts, listening parties, showcases, festivals, and more — all from one place',
    },
    drafts: {
      title: 'Your ideas start here',
      subtitle:
        "Draft events you're still working on will appear here until you're ready to publish them",
    },
    published: {
      title: 'No published events yet',
      subtitle: 'Published events will appear here. Create your first event to get started.',
    },
  }[tab];

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {/* Icon */}
      <div className="w-20 h-20 rounded-2xl bg-zinc-900 border border-white/5 flex items-center justify-center mb-5">
        <div className="w-14 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
          <div className="w-10 h-1.5 rounded-full bg-zinc-700" />
        </div>
      </div>

      <h3 className="text-white text-lg font-semibold mb-1">{config.title}</h3>
      <p className="text-zinc-500 text-sm max-w-sm mx-auto mb-6 leading-relaxed">
        {config.subtitle}
      </p>

      <button
        onClick={() => navigate({ to: '/events/create' })}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors"
      >
        <FiCalendar size={16} />
        Create Event
      </button>
    </div>
  );
};
